/**
 * HUD do jogo (item 4.5): placar com relógio, faixas de fase, jogadas, painel do atleta, controles e minimapa.
 * Usado pelo treino livre (`#voo`) e pela partida (`#partida`).
 */
import { useEffect, useRef, useState, type RefObject } from "react";
import {
  ABILITY_LABELS,
  ABILITY_OF,
  BALL,
  GRAPHICS_PRESETS,
  MATCH,
  NET,
  PITCH,
  POSITIONS,
  hoopCenters,
  type Kit,
  type Position,
} from "@besenliga/shared";
import type { Team, WorldState } from "@besenliga/sim";
import { browserStore } from "../creator/roster";
import { loadGraphics } from "../stadium/graphics";
import {
  FlightView,
  PLAYER_ID,
  PREVIEW_SPARK_SECONDS,
  type Clubs,
  type FlightHud,
  type PilotSetup,
  type PreviewMode,
  type ViewOptions,
} from "./view";
import type { ScenarioHud } from "../training/scenario";

const MODELS_URL = `${import.meta.env.BASE_URL}modelos/`;

/** O que a ação faz em cada posição, para a dica na tela. */
export const ACTION_HINT: Record<Position, string> = {
  artilheiro: "Pegue a Pelota voando até ela. Clique para passar; segure e solte para arremessar no aro.",
  batedor: "Chegue perto de um Petardo e clique para rebatê-lo no adversário à frente.",
  cacador: "Chegue perto da Faísca e clique para capturar.",
  goleiro: "Na sua área você segura a Pelota; fora dela, clique perto da bola para socá-la.",
};

/** Relógio mm:ss a partir de ticks. */
export function clockText(ticks: number): string {
  const s = Math.max(0, Math.ceil(ticks / NET.simTickHz));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export const END_REASON: Record<string, string> = {
  faisca: "A Faísca foi capturada",
  tempo: "Fim do tempo",
  "morte-subita": "Gol na morte súbita",
  empate: "Ninguém marcou na morte súbita",
};

/** Faixa no meio da tela nas paradas da partida. */
export function phaseBanner(
  m: NonNullable<FlightHud["match"]>,
  names: Record<Team, string>,
): { title: string; detail?: string } | null {
  switch (m.phase) {
    case "inicio":
      return { title: `Começa em ${Math.max(1, Math.ceil(m.phaseTicks / NET.simTickHz))}` };
    case "gol":
      return { title: "Gol!" };
    case "penalti":
      return {
        title: `Pênalti para os ${m.penaltyTeam ? names[m.penaltyTeam] : ""}`,
        detail: `${Math.ceil(m.phaseTicks / NET.simTickHz)} s para cobrar`,
      };
    case "fim": {
      const title = m.winner === "empate" || !m.winner ? "Empate" : `Vitória dos ${names[m.winner]}`;
      return { title, detail: END_REASON[m.endReason ?? "tempo"] ?? "" };
    }
    default:
      return null;
  }
}

export type ViewState = "carregando" | "pronto" | "erro";

/**
 * Monta a cena 3D num canvas novo dentro de `holder` e lê o HUD 10 vezes por segundo. Uma montagem por
 * combinação de parâmetros; `restartKey` força começar de novo.
 */
export function useFlightView(
  holder: RefObject<HTMLDivElement | null>,
  pilot: PilotSetup,
  opts: ViewOptions,
  restartKey = 0,
): { state: ViewState; hud: FlightHud | null; view: FlightView | null; scenario: ScenarioHud | null } {
  const [state, setState] = useState<ViewState>("carregando");
  const [scenario, setScenario] = useState<ScenarioHud | null>(null);
  const [hud, setHud] = useState<FlightHud | null>(null);
  const [view, setView] = useState<FlightView | null>(null);

  useEffect(() => {
    const el = holder.current;
    if (!el) return;
    const canvas = document.createElement("canvas");
    canvas.className = "stadium-canvas";
    canvas.setAttribute("aria-label", "Jogo em 3D. Use WASD, Espaço, Shift, Q, E e o mouse, ou um gamepad.");
    el.appendChild(canvas);
    const settings = GRAPHICS_PRESETS[loadGraphics(browserStore()).preset];
    let v: FlightView;
    setState("carregando");
    try {
      v = new FlightView(canvas, settings, pilot, opts);
    } catch {
      setState("erro");
      canvas.remove();
      return;
    }
    // Em desenvolvimento, os testes de navegador leem e avançam a partida por aqui.
    if (import.meta.env.DEV) Object.assign(canvas, { treino: v });
    setView(v);
    let alive = true;
    v.loadAthletes(MODELS_URL).then(
      () => alive && setState("pronto"),
      (e: unknown) => {
        console.error("Atletas não carregaram", e);
        if (alive) setState("erro");
      },
    );
    const id = setInterval(() => {
      setHud({
        ...v.hud,
        feed: [...v.hud.feed],
        score: { ...v.hud.score },
        match: v.hud.match ? { ...v.hud.match } : null,
        ability: { ...v.hud.ability },
      });
      setScenario(v.scenario ? v.scenario.hud() : null);
    }, 100);
    return () => {
      alive = false;
      clearInterval(id);
      setView(null);
      v.dispose();
      canvas.remove();
    };
  }, [holder, pilot, opts, restartKey]);

  return { state, hud, view, scenario };
}

const hex = (c: number) => `#${c.toString(16).padStart(6, "0")}`;

export function Scoreboard({ hud, clubs }: { hud: FlightHud; clubs: Clubs }) {
  const m = hud.match;
  return (
    <p className="flight-score" aria-label="Placar">
      <span style={{ background: hex(clubs.casa.kit.primary) }}>
        {clubs.casa.name} {hud.score.casa}
      </span>
      {m && (
        <span className="clock" data-state={m.suddenDeath ? "morte-subita" : "normal"}>
          {m.suddenDeath ? `Morte súbita ${clockText(m.suddenDeathTicks)}` : clockText(m.clockTicks)}
        </span>
      )}
      <span style={{ background: hex(clubs.fora.kit.primary) }}>
        {hud.score.fora} {clubs.fora.name}
      </span>
    </p>
  );
}

export function Banner({
  hud,
  clubs,
  children,
}: {
  hud: FlightHud;
  clubs: Clubs;
  children?: React.ReactNode;
}) {
  const m = hud.match;
  const banner = m ? phaseBanner(m, { casa: clubs.casa.name, fora: clubs.fora.name }) : null;
  if (!banner) return null;
  return (
    <div className="flight-banner" role="status" data-phase={m?.phase}>
      <strong>{banner.title}</strong>
      {banner.detail && <span>{banner.detail}</span>}
      {children}
    </div>
  );
}

export function Feed({ hud }: { hud: FlightHud }) {
  const feed = hud.feed.filter((f) => hud.time - f.at < 4);
  if (feed.length === 0) return null;
  return (
    <ul className="flight-feed" aria-live="polite">
      {feed.map((f) => (
        <li key={f.id}>{f.text}</li>
      ))}
    </ul>
  );
}

/** Quando a Faísca aparece em cada modo, para a dica do caçador. */
const sparkHint = (mode: PreviewMode) =>
  mode === "treino"
    ? `A Faísca aparece aos ${PREVIEW_SPARK_SECONDS} s.`
    : `A Faísca aparece aos ${MATCH.sparkAppearsAtSeconds / 60} min de jogo.`;

export function PilotPanel({
  hud,
  name,
  role,
  mode,
}: {
  hud: FlightHud;
  name: string;
  role: Position;
  mode: PreviewMode;
}) {
  const ability = ABILITY_LABELS[ABILITY_OF[role]];
  return (
    <section className="flight-hud" aria-label="Painel do atleta">
      <p className="flight-pilot">
        {name} · {POSITIONS[role].label}
      </p>
      <div className="stamina" data-state={hud.exhausted ? "esgotado" : hud.turbo ? "turbo" : "normal"}>
        <span className="stamina-label">
          {hud.stunned ? "Atordoado!" : hud.exhausted ? "Stamina esgotada" : hud.turbo ? "Turbo" : "Stamina"}
        </span>
        <span
          className="stamina-bar"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(hud.stamina * 100)}
          aria-label="Stamina"
        >
          <span style={{ width: `${hud.stamina * 100}%` }} />
        </span>
      </div>
      {hud.hasBall && (
        <div className="stamina charge" data-state={hud.charge > 0 ? "turbo" : "normal"}>
          <span className="stamina-label">{hud.charge > 0 ? "Carregando o arremesso" : "Com a Pelota"}</span>
          <span className="stamina-bar" aria-hidden="true">
            <span style={{ width: `${hud.charge * 100}%` }} />
          </span>
        </div>
      )}
      <div
        className="stamina ability"
        data-state={hud.ability.active ? "turbo" : hud.ability.ready >= 1 ? "pronta" : "recarga"}
      >
        <span className="stamina-label">
          <kbd>E</kbd> {ability.name}
          {hud.ability.active ? " · valendo!" : hud.ability.ready >= 1 ? " · pronta" : ""}
        </span>
        <span
          className="stamina-bar"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(hud.ability.ready * 100)}
          aria-label="Recarga da habilidade"
        >
          <span style={{ width: `${hud.ability.ready * 100}%` }} />
        </span>
      </div>
      <p className="flight-numbers">
        <span>{hud.speed} km/h</span>
        <span>{hud.height} m de altura</span>
        {hud.sparkActive && <span className="spark">Faísca em campo</span>}
      </p>
      <p className="flight-tip">
        {role === "cacador" && `${sparkHint(mode)} `}
        {ACTION_HINT[role]} Habilidade: {ability.hint}.
      </p>
    </section>
  );
}

export function ControlsHelp() {
  return (
    <aside className="flight-help" aria-label="Controles">
      <p>
        <kbd>W</kbd>
        <kbd>A</kbd>
        <kbd>S</kbd>
        <kbd>D</kbd> voar · <kbd>Espaço</kbd> subir · <kbd>Shift</kbd> descer · <kbd>Q</kbd> turbo
      </p>
      <p>
        Clique (ou <kbd>F</kbd>) ação · segurar carrega o arremesso · clique direito pede a bola
      </p>
      <p>
        <kbd>E</kbd> habilidade · <kbd>1</kbd>–<kbd>4</kbd> chat rápido · roda do mouse aproxima
      </p>
      <p>
        Gamepad: analógico, RT/LT sobe/desce, LB turbo, A ação, X carrega, B pede, Y habilidade, direcional
        chat
      </p>
    </aside>
  );
}

/** Minimapa do campo visto de cima (GDD seção 5): atletas, Pelota, Petardos e Faísca. */
export function Minimap({ view, kits }: { view: FlightView | null; kits: Record<Team, Kit> }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !view) return;
    let frame = 0;
    const draw = (time: number) => {
      frame = requestAnimationFrame(draw);
      drawMinimap(canvas, view.state, kits, time / 1000, view.me);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [view, kits]);
  return <canvas ref={ref} className="minimap" width={220} height={146} aria-label="Minimapa" role="img" />;
}

/** Desenha o minimapa: x do campo vira x da tela; z vira y. Exportado para teste. */
export function drawMinimap(
  canvas: HTMLCanvasElement,
  w: Readonly<WorldState>,
  kits: Record<Team, Kit>,
  time: number,
  me: string = PLAYER_ID,
) {
  const g = canvas.getContext("2d");
  if (!g) return;
  const W = canvas.width;
  const H = canvas.height;
  const pad = 6;
  const sx = (W / 2 - pad) / PITCH.sandRadiusX;
  const sz = (H / 2 - pad) / PITCH.sandRadiusZ;
  const px = (x: number) => W / 2 + x * sx;
  const pz = (z: number) => H / 2 + z * sz;
  g.clearRect(0, 0, W, H);
  // Areia, gramado, meio e áreas.
  g.fillStyle = "rgba(29, 26, 43, 0.72)";
  g.beginPath();
  g.ellipse(W / 2, H / 2, PITCH.sandRadiusX * sx, PITCH.sandRadiusZ * sz, 0, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = "rgba(79, 154, 74, 0.85)";
  g.beginPath();
  g.ellipse(W / 2, H / 2, PITCH.radiusX * sx, PITCH.radiusZ * sz, 0, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = "rgba(255, 255, 255, 0.55)";
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(W / 2, pz(-PITCH.radiusZ));
  g.lineTo(W / 2, pz(PITCH.radiusZ));
  g.stroke();
  g.beginPath();
  g.ellipse(W / 2, H / 2, PITCH.centerCircleRadius * sx, PITCH.centerCircleRadius * sz, 0, 0, Math.PI * 2);
  g.stroke();
  for (const side of [-1, 1] as const) {
    g.beginPath();
    g.ellipse(
      px(side * (PITCH.hoopX - 1)),
      H / 2,
      PITCH.keeperAreaX * sx,
      PITCH.keeperAreaZ * sz,
      0,
      side < 0 ? -Math.PI / 2 : Math.PI / 2,
      side < 0 ? Math.PI / 2 : (Math.PI * 3) / 2,
    );
    g.stroke();
    g.fillStyle = "#ffffff";
    for (const h of hoopCenters(side)) g.fillRect(px(h.x) - 1, pz(h.z) - 2.5, 2, 5);
  }
  const dot = (x: number, z: number, r: number, fill: string, stroke?: string) => {
    g.beginPath();
    g.arc(px(x), pz(z), r, 0, Math.PI * 2);
    g.fillStyle = fill;
    g.fill();
    if (stroke) {
      g.strokeStyle = stroke;
      g.lineWidth = 1.5;
      g.stroke();
    }
  };
  // Atletas: quanto mais alto, maior o ponto. O seu tem contorno dourado e a direção da vassoura.
  for (const a of w.athletes) {
    const r = 2.6 + (a.position.y / PITCH.ceiling) * 2;
    const you = a.id === me;
    dot(a.position.x, a.position.z, r, hex(kits[a.team].primary), you ? "#f2a541" : "rgba(255,255,255,0.8)");
    if (you) {
      g.strokeStyle = "#f2a541";
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(px(a.position.x), pz(a.position.z));
      g.lineTo(px(a.position.x + a.heading.x * 7), pz(a.position.z + a.heading.z * 7));
      g.stroke();
    }
  }
  const b = w.balls;
  if (!b) return;
  for (const p of b.petardos) dot(p.position.x, p.position.z, 2.2, "#2e2a35", "rgba(255,255,255,0.7)");
  dot(b.pelota.position.x, b.pelota.position.z, 2.6 + BALL.pelota.radius, "#d2452f", "#ffffff");
  if (b.faisca.active && Math.floor(time * 4) % 2 === 0)
    dot(b.faisca.position.x, b.faisca.position.z, 3, "#ffd36b", "#fff4d6");
}
